import React, { useEffect, useState } from "react";
import axios from "axios";
import { SealCheck, CaretUp, CaretDown } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Change = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-[#9CA3AF] text-xs">—</span>;
  const up = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono text-xs ${up ? "text-[#059669]" : value < 0 ? "text-[#DC2626]" : "text-[#4B5563]"}`}>
      {up ? <CaretUp size={11} weight="fill" /> : value < 0 ? <CaretDown size={11} weight="fill" /> : null}
      {up ? "+" : ""}{value.toFixed(2)}
    </span>
  );
};

export const USDAOfficialPrices = () => {
  const [boxedBeef, setBoxedBeef] = useState(null);
  const [fedCattle, setFedCattle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/usa/usda/boxed_beef`, { timeout: 60000 }),
      axios.get(`${API}/usa/usda/fed_cattle`, { timeout: 60000 }),
    ]).then(([bb, fc]) => {
      if (bb.status === "fulfilled") setBoxedBeef(bb.value.data);
      if (fc.status === "fulfilled") setFedCattle(fc.value.data);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <div className="bg-white border border-[#E0D4C8] p-6 mb-6 animate-pulse" data-testid="usda-loading"><div className="h-5 bg-[#E5E7EB] w-1/3 mb-4"></div><div className="h-20 bg-[#F3F4F6]"></div></div>;
  }

  const bbOk = boxedBeef?.available;
  const fcOk = fedCattle?.available;
  if (!bbOk && !fcOk) {
    return (
      <div className="bg-[#FEF3C7] border border-[#FCD34D] px-4 py-3 mb-6" data-testid="usda-unavailable">
        <p className="text-sm text-[#92400E]">Official USDA feed temporarily unavailable — showing indicative boards below.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-[#B91C1C] mb-6" data-testid="usda-official-prices">
      <div className="px-4 py-3 bg-[#B91C1C] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <SealCheck size={20} className="text-[#FCA5A5]" weight="fill" />
          <h2 className="font-serif text-lg text-white">Official USDA Prices</h2>
          <span className="px-2 py-0.5 bg-[#FCA5A5] text-[#450A0A] text-[10px] font-mono uppercase tracking-wide" data-testid="usda-official-tag">Official</span>
        </div>
        <span className="text-xs font-mono text-[#FECACA]">USDA AMS Market News</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#E0D4C8]">
        {bbOk && (
          <div className="p-4" data-testid="usda-boxed-beef">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#33302E]">Boxed Beef Cutout</h3>
              <span className="text-[10px] font-mono text-[#9CA3AF]">{boxedBeef.date}</span>
            </div>
            <div className="flex gap-8">
              <div>
                <p className="text-[10px] font-mono uppercase text-[#6B6661]">Choice</p>
                <p className="text-2xl font-mono text-[#111827]">${boxedBeef.choice?.toFixed(2)}</p>
                <Change value={boxedBeef.choice_change} />
              </div>
              <div>
                <p className="text-[10px] font-mono uppercase text-[#6B6661]">Select</p>
                <p className="text-2xl font-mono text-[#111827]">${boxedBeef.select?.toFixed(2)}</p>
                <Change value={boxedBeef.select_change} />
              </div>
            </div>
            <p className="text-[10px] text-[#9CA3AF] mt-2">{boxedBeef.unit}</p>
          </div>
        )}
        {fcOk && (
          <div className="p-4" data-testid="usda-fed-cattle">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#33302E]">5-Area Fed Cattle (weekly)</h3>
              <span className="text-[10px] font-mono text-[#9CA3AF]">wk of {fedCattle.week_of}</span>
            </div>
            <div className="flex gap-8">
              {fedCattle.classes.map((c) => (
                <div key={c.class}>
                  <p className="text-[10px] font-mono uppercase text-[#6B6661]">{c.class}</p>
                  <p className="text-2xl font-mono text-[#111827]">${c.weighted_avg_price?.toFixed(2)}</p>
                  <p className="text-[10px] font-mono text-[#6B6661]">{c.head_count?.toLocaleString()} head · {c.avg_weight_lbs?.toLocaleString()} lbs</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[#9CA3AF] mt-2">{fedCattle.unit}</p>
          </div>
        )}
      </div>
      <div className="px-4 py-2 bg-[#F9FAFB] border-t border-[#E0D4C8]">
        <p className="text-[11px] text-[#6B6661]">Source: USDA AMS Market News — official reported prices, no estimation.</p>
      </div>
    </div>
  );
};

export default USDAOfficialPrices;
